import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import clsx from 'clsx';

export default function UserPredictions() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('partidos'); // 'partidos' o 'podio'
  const [userName, setUserName] = useState('');
  const [fullList, setFullList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userPodium, setUserPodium] = useState(null);
  const [realPodium, setRealPodium] = useState(null);

  const PUNTOS_VALORES = { pred_champion: 40, pred_subchampion: 20, pred_third: 10, pred_fourth: 10 };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const savedUser = JSON.parse(localStorage.getItem('user'));
        setCurrentUserId(savedUser?.id);
        const config = { headers: { Authorization: `Bearer ${token}` } };

        const [resMatches, resPreds, resPR] = await Promise.all([
          api.get('/matches', config),
          api.get(`/predictions/user/${userId}`, config),
          api.get('/tournament-results?tenantId=localhost', config)
        ]);

        setUserName(resPreds.data.userName || 'Usuario');
        const userPredictions = resPreds.data.predictions || [];

        if (userPredictions.length > 0 && userPredictions[0].user) {
          const u = userPredictions[0].user;
          setUserPodium({
            pred_champion: u.pred_champion,
            pred_subchampion: u.pred_subchampion,
            pred_third: u.pred_third,
            pred_fourth: u.pred_fourth
          });
        }
        
        setRealPodium(resPR.data);

        const merged = resMatches.data.map(match => {
          const found = userPredictions.find(p => p.match.id === match.id);
          return {
            id: found?.id || `temp-${match.id}`,
            predictedHomeScore: found ? found.predictedHomeScore : null,
            predictedAwayScore: found ? found.predictedAwayScore : null,
            points: found ? found.points : 0,
            match: match
          };
        }).sort((a, b) => new Date(a.match.matchDate) - new Date(b.match.matchDate));

        setFullList(merged);
      } catch (err) {
        console.error("Error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId]);

  if (loading) return <div className="p-20 text-center font-black uppercase text-[10px] tracking-widest animate-pulse text-slate-400">Cargando perfil...</div>;

  const ahora = new Date();
  const margen = 10 * 60 * 1000;
  const esPropio = String(currentUserId) === String(userId);
  const filtrados = fullList.filter(p => esPropio || (ahora.getTime() + margen) >= new Date(p.match.matchDate).getTime());

  // Cálculo total de puntos de podio
  const totalPuntosPodio = userPodium ? Object.keys(PUNTOS_VALORES).reduce((acc, key) => {
    return userPodium[key] === realPodium?.[key.replace('pred_', '')] ? acc + PUNTOS_VALORES[key] : acc;
  }, 0) : 0;

  return (
    <div className="min-h-screen bg-slate-50 pt-10 p-4 font-sans text-slate-900">
      <div className="max-w-2xl mx-auto bg-white rounded-[40px] shadow-2xl overflow-hidden border border-slate-200">
        
        {/* CABECERA DINÁMICA */}
        <div className="bg-slate-900 p-8 text-white relative">
          <button onClick={() => navigate(-1)} className="absolute top-8 right-8 bg-white/10 hover:bg-white/20 p-3 rounded-2xl transition-all">
            <span className="text-[10px] font-black uppercase tracking-widest">Volver</span>
          </button>
          <p className="text-blue-400 text-[10px] font-black uppercase tracking-[0.2em] mb-2">Perfil de Jugador</p>
          <h2 className="text-3xl font-black uppercase italic">{userName}</h2>
        </div>

        {/* SELECTOR DE SECCIÓN (TABS) */}
        <div className="flex border-b border-slate-100 bg-slate-50/50 p-2">
          <button 
            onClick={() => setActiveTab('partidos')}
            className={clsx("flex-1 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all", 
            activeTab === 'partidos' ? "bg-white text-blue-600 shadow-sm" : "text-slate-400 hover:text-slate-600")}
          >
            ⚽ Partidos
          </button>
          <button 
            onClick={() => setActiveTab('podio')}
            className={clsx("flex-1 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all", 
            activeTab === 'podio' ? "bg-white text-blue-600 shadow-sm" : "text-slate-400 hover:text-slate-600")}
          >
            🏆 Podio Final
          </button>
        </div>

        <div className="p-6">
          {/* CONTENIDO: PARTIDOS */}
          {activeTab === 'partidos' && (
            <div className="space-y-3 animate-in fade-in duration-500">
              {filtrados.map((p) => (
                <div key={p.id} className="flex items-center gap-4 p-3 bg-white rounded-2xl border border-slate-100 shadow-sm">
                  <div className="flex-1 flex items-center justify-end gap-3">
                    <span className="text-[9px] font-black uppercase text-slate-600 text-right">{p.match.homeTeam?.name}</span>
                    <img src={p.match.homeTeam?.flagUrl} className="w-7 h-4.5 object-cover rounded-sm shadow-sm" alt="" />
                  </div>
                  <div className="bg-slate-900 px-4 py-1.5 rounded-xl text-white font-black text-xs min-w-[65px] text-center shadow-lg shadow-slate-200">
                    {p.predictedHomeScore ?? '-'} : {p.predictedAwayScore ?? '-'}
                  </div>
                  <div className="flex-1 flex items-center gap-3">
                    <img src={p.match.awayTeam?.flagUrl} className="w-7 h-4.5 object-cover rounded-sm shadow-sm" alt="" />
                    <span className="text-[9px] font-black uppercase text-slate-600">{p.match.awayTeam?.name}</span>
                  </div>
                  <div className="border-l border-slate-100 pl-4 text-center min-w-[45px]">
                    <p className={clsx("text-[11px] font-black", p.points > 0 ? "text-green-600" : "text-slate-300")}>+{p.points}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CONTENIDO: PODIO */}
          {activeTab === 'podio' && (
            <div className="animate-in slide-in-from-bottom-4 duration-500 space-y-6">
              <div className="bg-blue-50/50 border border-blue-100 p-6 rounded-[32px] text-center">
                <span className="text-[9px] font-black text-blue-400 uppercase tracking-widest">Puntos ganados en podio</span>
                <p className="text-4xl font-black text-blue-600 mt-1">+{totalPuntosPodio} <span className="text-sm">PTS</span></p>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {[
                  { id: 'pred_champion', label: '🥇 Campeón', rKey: 'champion', pts: 40 },
                  { id: 'pred_subchampion', label: '🥈 Subcampeón', rKey: 'subchampion', pts: 20 },
                  { id: 'pred_third', label: '🥉 Tercer Puesto', rKey: 'third', pts: 10 },
                  { id: 'pred_fourth', label: '🏅 Cuarto Puesto', rKey: 'fourth', pts: 10 }
                ].map((pos) => {
                  const val = userPodium?.[pos.id];
                  const real = realPodium?.[pos.rKey];
                  const isCorrect = real && val === real;
                  const hasReal = !!real;

                  return (
                    <div key={pos.id} className={clsx("p-5 rounded-3xl border-2 flex items-center justify-between transition-all",
                      hasReal ? (isCorrect ? "border-green-500 bg-green-50/30" : "border-red-100 bg-slate-50 opacity-60") : "border-slate-100 bg-white")}>
                      <div className="flex flex-col">
                        <span className="text-[9px] font-black text-slate-400 uppercase mb-1">{pos.label}</span>
                        <span className="text-[13px] font-black uppercase text-slate-800">{val || 'Sin elección'}</span>
                        {hasReal && !isCorrect && (
                          <span className="text-[8px] font-bold text-red-400 mt-1 uppercase italic">Real: {real}</span>
                        )}
                      </div>
                      <div className="text-right">
                        {hasReal && (
                          <div className={clsx("text-[10px] font-black px-3 py-1 rounded-lg uppercase shadow-sm", 
                            isCorrect ? "bg-green-600 text-white" : "bg-red-50 text-red-400")}>
                            {isCorrect ? `+${pos.pts} pts` : '0 pts'}
                          </div>
                        )}
                        {!hasReal && <span className="text-slate-200 text-xs">Pendiente</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}